import React, { useContext, useEffect } from 'react';
import {
  Route,
  Switch,
  useParams,
  useLocation,
  BrowserRouter,
} from 'react-router-dom'; // TODO: its v5, migrate to v6

import Referral from './containers/Referral';
import { uxTracking } from './util/ux-tracking';
import { AppContext } from './containers/AppContext';
import HtmlHead from './components/UI/Head/HtmlHead';
import FlytreeReview from './containers/FlytreeReview'; 
import RepairyReview from './containers/RepairyReview';
import ReferralRedeem from './containers/ReferralRedeem';
import { RpryMedia } from './components/RpryMedia/RpryMedia';
import ReviewExample from './components/Review/ReviewExample';
import RedirectingToBookingFeedback from './feedback-redirect';
import { RpryShortly } from './components/RpryShrtly/RpryShrtly';
import { RpryPayment } from './components/RpryPayment/RpryPayment';
import { RpryEstimation } from './components/RpryEstimation/RpryEstimation';
import { DashBookingDetail } from './components/BookingRequest/BookingRequest';
import { ShortCodeBooking } from './components/ShortCodeBooking/ShortCodeBooking';
import NotificationUnsubs, {
  UnsubscribeDone
} from './components/NotificationUnsubs';
import {
  AppName,
  referralPrefix,
  RepairyBookingURL,
  referralRedeemPrefix,
} from './util/lib';

import css from './App.module.css';

const AppMode = process.env.REACT_APP_MODE || 'booking';

function App() {
  const { theme } = useContext(AppContext);

  const updateHeight = async () => {
    // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
    let vh = window.innerHeight * 0.01;
    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`);

    // We listen to the resize event
    window.addEventListener('resize', () => {
      // We execute the same script as before
      let vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    });
  };
  useEffect(() => {
    updateHeight();
    uxTracking.init();
  }, []);

  if (AppName && AppName.toLowerCase() === 'flytree') {
    return (
      <BrowserRouter>
        <div
          style={theme}
          className={css.App}>
          <HtmlHead flytree />
          <Switch>
            <Route
              path="/"
              exact
              component={() => {
                window.location.href = 'https://www.flytree.co';
                return null;
              }}
            >
            </Route>
            <Route path={[
              `${referralPrefix}/:code/share/:refCode`,
              `${referralPrefix}/:code`,
            ]}>
              <Referral />
            </Route>
            <Route path={`${referralRedeemPrefix}/:code`}>
              <ReferralRedeem />
            </Route>
            <Route exact path='/1234567890'>
              <ReviewExample />
            </Route>
            <Route path="/:code">
              <FlytreeReview />
            </Route>
          </Switch>
        </div>
      </BrowserRouter>
    );
  }
  return (
    <BrowserRouter>
      <div className={css.App}>
        <HtmlHead repairy />
        <Switch>
          <Route path="/i/:id/:code">
            <DashBookingDetail prefix="details" />
          </Route>
          <Route path="/d/:id/:code">
            <DashBookingDetail prefix="details" />
          </Route>
          <Route path="/br/:id/:code">
            <DashBookingDetail prefix="requests" />
          </Route>
          <Route path="/s/:id">
            <RedirectingToBookingConfirmation />
          </Route>
          <Route path="/r/:code">
            <RepairyReview />
          </Route>
          <Route path="/f/:code">
            <RedirectingToBookingFeedback />
          </Route>
          <Route path="/m/:code">
            <RpryMedia />
          </Route>
          <Route path="/off/:id">
            <NotificationUnsubs />
          </Route>
          <Route path="/shrtly/:code">
            <RpryShortly />
          </Route>
          <Route path="/estimation/:code">
            <RpryEstimation />
          </Route>
          <Route path="/pay/:paymentId">
            <RpryPayment />
          </Route>
          <Route path="/unsubscribed">
            <UnsubscribeDone />
          </Route>
          <Route path="/:id/rc/:customerId">
            <RedirectingToReturningCustomer />
          </Route>
          <Route path="/:id/:code/:action?">
            <ShortCodeBooking />
          </Route>
          <Route path="/:id">
            <GenericHandler />
          </Route>
          <Route
            path="/"
            exact
            component={() => {
              window.location.href = 'https://www.repairy.com.au/';
              return null;
            }}
          />
        </Switch>
      </div>
    </BrowserRouter>
  );
}

const Redirecting = () => {
  return (
    <>
      <HtmlHead repairy />
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <p>
          Redirecting...
        </p>
      </div>
    </>
  )
}

function RedirectingToBooking() {
  const { id, code } = useParams();
  const location = useLocation();
  const uris = [RepairyBookingURL, '/', id];
  if (code) {
    uris.push(...['/', code]);
  }
  const baseUrl = uris.join('');
  const queryString = location.search;
  const redirectUrl = queryString ? `${baseUrl}${queryString}` : baseUrl;
  window.location.href = redirectUrl;
  return (
    <Redirecting />
  );
}

function GenericHandler() {
  const { id, code } = useParams();
  // handle est.repairy.au/:id   (id: estimation code)
  //   -> book.repairy.au/:id/estimation/:code (id: shop code, code: estimation code)
  if (AppMode === 'estimation' && id && !code) {
    return <RpryEstimation />;
  }
  // handle everything else
  return <RedirectingToBooking />;
}

function RedirectingToBookingConfirmation() {
  const { id } = useParams();
  const location = useLocation();
  const baseUrl = [RepairyBookingURL, '/s/', id].join('');
  const queryString = location.search;
  const redirectUrl = queryString ? `${baseUrl}${queryString}` : baseUrl;
  window.location.href = redirectUrl;
  return (
    <Redirecting />
  );
}

function RedirectingToReturningCustomer() {
  const { id, customerId } = useParams();
  const location = useLocation();
  const baseUrl = [
    RepairyBookingURL,
    '/',
    id,
    '/regular/',
    customerId,
  ].join('');
  const queryString = location.search;
  const redirectUrl = queryString ? `${baseUrl}${queryString}` : baseUrl;
  window.location.href = redirectUrl;
  return (
    <Redirecting />
  );
}

export default App;
